import {
  DiscountType,
  DiscountUnit,
} from "@/features/cart/types/discount.type";
import { FiltersDietsType } from "@/features/filters/types/filters-diets.type";
import { type FiltersSetup } from "@/features/filters/types/filters-setup.type";
import { FieldValues } from "react-hook-form";

const FIRST_PAGE = 1;

// A `page` query parameter straight from the URL. Anything but a positive
// integer (`?page='`, `?page=0`, `?page=abc`) used to reach catalog as is,
// which answered 400 "Page should not be less than 1" and took the whole
// listing page down with a 500 (Sentry WEB-216).
export const normalizePageParam = (value: unknown): number => {
  const page =
    typeof value === "number"
      ? value
      : Number.parseInt(String(value ?? ""), 10);

  return Number.isInteger(page) && page >= FIRST_PAGE ? page : FIRST_PAGE;
};

export const parseCaloricityParams = (
  params: Partial<FiltersDietsType>,
  filtersSetup: FiltersSetup
) => {
  if ("caloricity" in params) {
    const calories = filtersSetup.calories.find(
      (calories) => calories.label === params.caloricity
    );
    if (calories) {
      delete params.caloricity;
      return {
        ...params,
        "calories[gte]": calories.from.toString(),
        "calories[lte]": calories.to.toString(),
      };
    }
  }

  return params;
};

export const trimObjectValues = (obj: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      typeof value === "string" ? value.trim() : value,
    ])
  );

export const sumDiscounts = (discounts: DiscountType[], t: any) => {
  if (!discounts || discounts.length === 0) {
    return [];
  }

  const summedDiscount = discounts.reduce(
    (acc, curr) => ({
      name: t("cart.configuration.totalDiscount"),
      amount: {
        gross: acc.amount.gross + curr.amount.gross,
        net: acc.amount.net + curr.amount.net,
        tax: acc.amount.tax + curr.amount.tax,
      },
      type: curr.type,
      unit: curr.unit || "percentage",
      percentage: curr.percentage || 0,
    }),
    {
      name: t("cart.configuration.totalDiscount"),
      amount: { gross: 0, net: 0, tax: 0 },
      percentage: 0,
      unit: "percentage" as DiscountUnit,
    }
  );

  return [summedDiscount];
};


export const serializeFormValues = (
  values: FieldValues,
): Record<string, string | number | undefined> => {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      if (Array.isArray(value)) {
        return [key, value.join(",")];
      }

      if (typeof value === "string" || typeof value === "number") {
        return [key, value];
      }

      if (typeof value === "boolean") {
        return [key, String(value)];
      }

      return [key, undefined];
    }),
  );
};